import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Link,
  withRouter,
} from "react-router-dom";

import logo from "../assets/logo.png";
import logo2 from "../assets/logo2.png";

import "./CSS/ImageConverter.css";
import "./CSS/StripeQR100.css";

const StripeQR100 = ({ history }) => {
  const [qrImage, setQrImage] = useState("");

  const [
    hostedInstructionsUrl,
    setHostedInstructionsUrl,
  ] = useState("");

  const [
    paymentIntentId,
    setPaymentIntentId,
  ] = useState("");

  const [
    paymentStatus,
    setPaymentStatus,
  ] = useState("");

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  /*
   * Replace this with the logged-in user's real email
   * when you connect the payment page to your user account.
   */
  const customerEmail = "customer@example.com";

  const pollingIntervalRef = useRef(null);
  const redirectTimerRef = useRef(null);
  const requestStartedRef = useRef(false);

  /*
   * Stop checking the Stripe payment status.
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(
        pollingIntervalRef.current
      );

      pollingIntervalRef.current = null;
    }
  }, []);

  /*
   * Stop the success-page redirect timer.
   */
  const stopRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(
        redirectTimerRef.current
      );

      redirectTimerRef.current = null;
    }
  }, []);

  /*
   * Create a new Stripe PromptPay PaymentIntent.
   *
   * Relative API paths are used because your React
   * frontend and Express backend both run on port 3000.
   */
  const createPromptPayPayment =
    useCallback(async () => {
      if (creating) {
        return;
      }

      setCreating(true);
      setLoading(true);
      setError("");
      setPaymentStatus("");
      setQrImage("");
      setHostedInstructionsUrl("");
      setPaymentIntentId("");

      stopPolling();
      stopRedirectTimer();

      try {
        const response = await fetch(
          "/create-promptpay-payment",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              email: customerEmail,
            }),
          }
        );

        const contentType =
          response.headers.get(
            "content-type"
          ) || "";

        if (
          !contentType.includes(
            "application/json"
          )
        ) {
          const responseText =
            await response.text();

          throw new Error(
            `The backend did not return JSON. Response: ${responseText.slice(
              0,
              150
            )}`
          );
        }

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error ||
              "Unable to generate the PromptPay QR code."
          );
        }

        if (!data.qrCodePng) {
          throw new Error(
            "The backend did not return qrCodePng."
          );
        }

        if (!data.paymentIntentId) {
          throw new Error(
            "The backend did not return paymentIntentId."
          );
        }

        setQrImage(data.qrCodePng);

        setHostedInstructionsUrl(
          data.hostedInstructionsUrl || ""
        );

        setPaymentIntentId(
          data.paymentIntentId
        );

        setPaymentStatus(
          data.status || "requires_action"
        );
      } catch (requestError) {
        console.error(
          "Create PromptPay payment error:",
          requestError
        );

        setError(
          requestError.message ||
            "Unable to generate the payment QR code."
        );
      } finally {
        setLoading(false);
        setCreating(false);
      }
    }, [
      creating,
      stopPolling,
      stopRedirectTimer,
    ]);

  /*
   * Automatically create the PromptPay QR code
   * when the component first renders.
   */
  useEffect(() => {
    if (requestStartedRef.current) {
      return;
    }

    requestStartedRef.current = true;

    createPromptPayPayment();
  }, [createPromptPayPayment]);

  /*
   * Poll the backend every three seconds
   * to check whether Stripe has confirmed payment.
   */
  useEffect(() => {
    if (!paymentIntentId) {
      return undefined;
    }

    let componentMounted = true;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(
          `/payment-status/${encodeURIComponent(
            paymentIntentId
          )}`
        );

        const contentType =
          response.headers.get(
            "content-type"
          ) || "";

        if (
          !contentType.includes(
            "application/json"
          )
        ) {
          throw new Error(
            "The payment-status endpoint did not return JSON."
          );
        }

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.error ||
              "Unable to check payment status."
          );
        }

        if (!componentMounted) {
          return;
        }

        setPaymentStatus(data.status);

        /*
         * Payment completed successfully.
         *
         * Stop polling, display the green success
         * message for two seconds, then redirect.
         */
        if (data.status === "succeeded") {
          stopPolling();
          stopRedirectTimer();

          setPaymentStatus("succeeded");

          redirectTimerRef.current =
            setTimeout(() => {
              history.replace(
                "/logincredits100"
              );
            }, 2000);

          return;
        }

        /*
         * Stop polling when this PaymentIntent
         * can no longer be completed.
         */
        if (
          data.status === "canceled" ||
          data.status ===
            "requires_payment_method"
        ) {
          stopPolling();
        }
      } catch (statusError) {
        console.error(
          "Payment status error:",
          statusError
        );
      }
    };

    checkPaymentStatus();

    pollingIntervalRef.current =
      setInterval(
        checkPaymentStatus,
        3000
      );

    return () => {
      componentMounted = false;

      stopPolling();
    };
  }, [
    history,
    paymentIntentId,
    stopPolling,
    stopRedirectTimer,
  ]);

  /*
   * Clean up timers when leaving the page.
   */
  useEffect(() => {
    return () => {
      stopPolling();
      stopRedirectTimer();
    };
  }, [
    stopPolling,
    stopRedirectTimer,
  ]);

  /*
   * Generate a replacement PromptPay QR code.
   */
  const handleGenerateNewQR = () => {
    createPromptPayPayment();
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case "succeeded":
        return "Payment successful! Your 100 credits are ready.";

      case "processing":
        return "Stripe is processing your payment...";

      case "requires_action":
        return "Scan the QR code with your Thai banking app.";

      case "requires_payment_method":
        return "Payment was not completed. Please generate a new QR code.";

      case "canceled":
        return "This payment was canceled.";

      default:
        return "Waiting for payment...";
    }
  };

  const isSuccessful =
    paymentStatus === "succeeded";

  return (
    <div className="payment-qr-page">
      <img
        className="logo"
        src={logo}
        alt="Company logo"
      />

      <div className="payment-card">
        <h1 className="payment-title">
          PromptPay Payment
        </h1>

        <div className="payment-amount">
          ฿100.00
        </div>

        {!isSuccessful && (
          <p className="payment-instruction">
            Open your Thai banking
            application and scan the Stripe
            PromptPay QR code.
          </p>
        )}

        <div className="qr-container">
          {loading && (
            <div className="payment-loading">
              <div className="payment-spinner" />

              <p>
                Generating secure QR code...
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="payment-error">
              <p>{error}</p>

              <button
                type="button"
                className="payment-button"
                onClick={
                  handleGenerateNewQR
                }
                disabled={creating}
              >
                {creating
                  ? "Generating..."
                  : "Try again"}
              </button>
            </div>
          )}

          {!loading &&
            !error &&
            qrImage &&
            !isSuccessful && (
              <img
                src={qrImage}
                alt="Stripe PromptPay QR code for 100 baht"
                className="promptpay-qr"
              />
            )}

          {isSuccessful && (
            <div className="payment-success-box">
              <div className="payment-success-icon">
                ✓
              </div>

              <h2>
                Payment Successful
              </h2>

              <p>
                Your payment has been
                confirmed.
              </p>

              <p>
                Redirecting to your credits
                page...
              </p>
            </div>
          )}
        </div>

        {!loading && !error && (
          <div
            className={
              isSuccessful
                ? "payment-status payment-status-success"
                : "payment-status"
            }
          >
            {getStatusMessage()}
          </div>
        )}

        {hostedInstructionsUrl &&
          !isSuccessful && (
            <a
              className="hosted-payment-link"
              href={
                hostedInstructionsUrl
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Stripe payment
              instructions
            </a>
          )}

        {!loading &&
          !error &&
          !isSuccessful && (
            <button
              type="button"
              className="payment-button secondary-button"
              onClick={
                handleGenerateNewQR
              }
              disabled={creating}
            >
              {creating
                ? "Generating..."
                : "Generate new QR code"}
            </button>
          )}

        {!isSuccessful && (
          <p className="payment-note">
            You do not need to upload a
            payment slip. Stripe confirms
            the payment automatically.
          </p>
        )}

        {!isSuccessful && (
          <Link
            className="back-link"
            to="/"
          >
            Return to home
          </Link>
        )}
      </div>

      <footer className="payment-footer">
        <div>Powered by</div>

        <img
          className="logo2"
          src={logo2}
          alt="New Wave Consulting Group logo"
        />

        <div>
          New Wave Consulting Group
        </div>
      </footer>
    </div>
  );
};

export default withRouter(StripeQR100);
